/**
 * Product Data Review System
 * ==========================
 * Notification/review interface for product master data exceptions.
 * 
 * Key principles:
 * - All corrections must be done manually by the business owner
 * - Status changes do NOT modify product data
 * - Original detected values are preserved as audit records
 */
import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

// UI Components
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

// Icons
import {
  Search, RefreshCw, ChevronLeft, ChevronRight,
  AlertTriangle, AlertCircle, Info, CheckCircle2, XCircle,
  Package, Eye, FileText, Scan, TrendingUp, Copy, Barcode,
  DollarSign, Tag, Clock, User, MessageSquare,
} from "lucide-react";

// ============================================================================
// Constants
// ============================================================================

const ISSUE_TYPE_CONFIG = {
  pp_greater_than_mrp: {
    label: "Purchase Price > MRP",
    shortLabel: "PP > MRP",
    icon: TrendingUp,
    color: "bg-orange-100 text-orange-700 border-orange-200",
  },
  potential_duplicate: {
    label: "Potential Duplicate",
    shortLabel: "Duplicate",
    icon: Copy,
    color: "bg-purple-100 text-purple-700 border-purple-200",
  },
  mrp_zero_or_missing: {
    label: "MRP Zero/Missing",
    shortLabel: "MRP = 0",
    icon: DollarSign,
    color: "bg-red-100 text-red-700 border-red-200",
  },
  shared_manual_barcode: {
    label: "Shared Barcode",
    shortLabel: "Shared BC",
    icon: Barcode,
    color: "bg-blue-100 text-blue-700 border-blue-200",
  },
};

const SEVERITY_CONFIG = {
  critical: { label: "Critical", color: "bg-red-500 text-white", icon: AlertCircle },
  high: { label: "High", color: "bg-orange-500 text-white", icon: AlertTriangle },
  medium: { label: "Medium", color: "bg-yellow-500 text-white", icon: AlertTriangle },
  low: { label: "Low", color: "bg-blue-500 text-white", icon: Info },
  info: { label: "Info", color: "bg-gray-500 text-white", icon: Info },
};

const STATUS_CONFIG = {
  new: { label: "New", color: "bg-blue-100 text-blue-700 border-blue-300" },
  under_review: { label: "Under Review", color: "bg-yellow-100 text-yellow-700 border-yellow-300" },
  reviewed: { label: "Reviewed", color: "bg-green-100 text-green-700 border-green-300" },
  dismissed: { label: "Dismissed", color: "bg-gray-100 text-gray-600 border-gray-300" },
};

// ============================================================================
// Component
// ============================================================================

export default function ProductDataReview() {
  const navigate = useNavigate();
  
  // State
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [scanning, setScanning] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [statusDialogOpen, setStatusDialogOpen] = useState(false);
  const [pendingStatus, setPendingStatus] = useState(null);
  const [notes, setNotes] = useState("");
  
  // Filters
  const [search, setSearch] = useState("");
  const [issueTypeFilter, setIssueTypeFilter] = useState("all");
  const [severityFilter, setSeverityFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  
  // Fetch data
  const fetchItems = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append("page", page);
      params.append("limit", 50);
      if (search) params.append("search", search);
      if (issueTypeFilter !== "all") params.append("issue_type", issueTypeFilter);
      if (severityFilter !== "all") params.append("severity", severityFilter);
      if (statusFilter !== "all") params.append("status", statusFilter);
      
      const { data } = await api.get(`/product-data-review/?${params}`);
      setItems(data.items || []);
      setTotalPages(data.pages || 1);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  }, [page, search, issueTypeFilter, severityFilter, statusFilter]);
  
  const fetchSummary = useCallback(async () => {
    try {
      const { data } = await api.get("/product-data-review/summary");
      setSummary(data);
    } catch (err) {
      console.error("Failed to fetch summary:", err);
    }
  }, []);
  
  useEffect(() => {
    fetchItems();
    fetchSummary();
  }, [fetchItems, fetchSummary]);
  
  // Scan for issues
  const handleScan = async () => {
    setScanning(true);
    try {
      const { data } = await api.post("/product-data-review/scan");
      toast.success(data.message);
      fetchItems();
      fetchSummary();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setScanning(false);
    }
  };
  
  // Open detail view
  const openDetail = async (item) => {
    try {
      const { data } = await api.get(`/product-data-review/${item.id}`);
      setSelectedItem(data);
      setNotes(data.notes || "");
      setDetailOpen(true);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };
  
  // Update status
  const handleStatusChange = async () => {
    if (!selectedItem || !pendingStatus) return;
    
    try {
      await api.patch(`/product-data-review/${selectedItem.id}/status`, {
        status: pendingStatus,
        notes: notes
      });
      toast.success(`Status updated to ${STATUS_CONFIG[pendingStatus].label}`);
      setStatusDialogOpen(false);
      setPendingStatus(null);
      fetchItems();
      fetchSummary();
      
      // Update selected item
      setSelectedItem(prev => ({
        ...prev,
        status: pendingStatus,
        notes: notes
      }));
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };
  
  // Save notes only
  const handleSaveNotes = async () => {
    if (!selectedItem) return;
    
    try {
      await api.patch(`/product-data-review/${selectedItem.id}/notes`, { notes });
      toast.success("Notes saved");
      setSelectedItem(prev => ({ ...prev, notes }));
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };
  
  // Navigate to product profile
  const viewProduct = (productId) => {
    navigate(`/inventory/products/${productId}`);
  };
  
  // Render summary cards
  const renderSummary = () => {
    if (!summary) return null;
    
    const statusCards = [
      { key: "new", icon: AlertCircle, color: "text-blue-600", bg: "bg-blue-50" },
      { key: "under_review", icon: Clock, color: "text-yellow-600", bg: "bg-yellow-50" },
      { key: "reviewed", icon: CheckCircle2, color: "text-green-600", bg: "bg-green-50" },
      { key: "dismissed", icon: XCircle, color: "text-gray-500", bg: "bg-gray-50" },
    ];
    
    return (
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        {statusCards.map(({ key, icon: Icon, color, bg }) => (
          <Card
            key={key}
            className={`cursor-pointer hover:shadow-md transition-shadow ${statusFilter === key ? "ring-2 ring-primary" : ""}`}
            onClick={() => setStatusFilter(statusFilter === key ? "all" : key)}
          >
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className={`p-2 rounded-lg ${bg}`}>
                  <Icon className={`h-5 w-5 ${color}`} />
                </div>
                <div>
                  <p className="text-2xl font-bold">{summary.by_status[key] || 0}</p>
                  <p className="text-xs text-muted-foreground">{STATUS_CONFIG[key].label}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    );
  };
  
  // Render category badges
  const renderCategoryBadges = () => {
    if (!summary) return null;
    
    return (
      <div className="flex flex-wrap gap-2 mb-4">
        <span className="text-sm text-muted-foreground mr-2">Categories:</span>
        {Object.entries(ISSUE_TYPE_CONFIG).map(([key, config]) => {
          const count = summary.by_type[key] || 0;
          const isActive = issueTypeFilter === key;
          return (
            <Badge
              key={key}
              variant="outline"
              className={`cursor-pointer ${isActive ? "ring-2 ring-primary" : ""} ${config.color}`}
              onClick={() => setIssueTypeFilter(isActive ? "all" : key)}
            >
              <config.icon className="h-3 w-3 mr-1" />
              {config.shortLabel}: {count}
            </Badge>
          );
        })}
      </div>
    );
  };
  
  // Render item row
  const renderItem = (item) => {
    const typeConfig = ISSUE_TYPE_CONFIG[item.issue_type] || {};
    const severityConfig = SEVERITY_CONFIG[item.severity] || {};
    const statusConfig = STATUS_CONFIG[item.status] || {};
    const TypeIcon = typeConfig.icon || Package;
    
    // Get primary product name
    const productName = item.detected_values?.product_name || 
                       item.detected_values?.products?.[0]?.product ||
                       "Unknown Product";
    
    return (
      <TableRow
        key={item.id}
        className="cursor-pointer hover:bg-muted/50"
        onClick={() => openDetail(item)}
        data-testid={`review-item-${item.id}`}
      >
        <TableCell>
          <Badge variant="outline" className={`${severityConfig.color} text-xs`}>
            {severityConfig.label}
          </Badge>
        </TableCell>
        <TableCell>
          <div className="flex items-center gap-2">
            <TypeIcon className="h-4 w-4 text-muted-foreground" />
            <span className="text-sm">{typeConfig.shortLabel}</span>
          </div>
        </TableCell>
        <TableCell className="max-w-[300px]">
          <p className="font-medium truncate">{productName}</p>
          <p className="text-xs text-muted-foreground truncate">
            {item.detected_values?.auto_barcode || 
             item.detected_values?.products?.map(p => p.auto_barcode).join(", ") ||
             "N/A"}
          </p>
        </TableCell>
        <TableCell className="max-w-[200px]">
          <p className="text-sm truncate">{item.reason}</p>
        </TableCell>
        <TableCell>
          <Badge variant="outline" className={statusConfig.color}>
            {statusConfig.label}
          </Badge>
        </TableCell>
        <TableCell className="text-sm text-muted-foreground">
          {new Date(item.detected_at).toLocaleDateString()}
        </TableCell>
        <TableCell>
          <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); openDetail(item); }}>
            <Eye className="h-4 w-4" />
          </Button>
        </TableCell>
      </TableRow>
    );
  };
  
  // Render detail sheet content
  const renderDetailContent = () => {
    if (!selectedItem) return null;
    
    const typeConfig = ISSUE_TYPE_CONFIG[selectedItem.issue_type] || {};
    const severityConfig = SEVERITY_CONFIG[selectedItem.severity] || {};
    const statusConfig = STATUS_CONFIG[selectedItem.status] || {};
    const TypeIcon = typeConfig.icon || Package;
    
    return (
      <ScrollArea className="h-full pr-4">
        {/* Header */}
        <div className="space-y-4 pb-4">
          <div className="flex items-center gap-2 flex-wrap">
            <Badge variant="outline" className={`${severityConfig.color}`}>
              {severityConfig.label}
            </Badge>
            <Badge variant="outline" className={typeConfig.color}>
              <TypeIcon className="h-3 w-3 mr-1" />
              {typeConfig.label}
            </Badge>
            <Badge variant="outline" className={statusConfig.color}>
              {statusConfig.label}
            </Badge>
          </div>
          
          <p className="text-sm font-medium">{selectedItem.reason}</p>
          
          <div className="text-xs text-muted-foreground">
            <p>Detected: {new Date(selectedItem.detected_at).toLocaleString()}</p>
            {selectedItem.reviewed_at && (
              <p>Reviewed: {new Date(selectedItem.reviewed_at).toLocaleString()} by {selectedItem.reviewed_by}</p>
            )}
          </div>
        </div>
        
        <Separator className="my-4" />
        
        {/* Detected Values */}
        <div className="space-y-4">
          <h4 className="font-semibold text-sm flex items-center gap-2">
            <FileText className="h-4 w-4" />
            Detected Values (at detection time)
          </h4>
          
          {selectedItem.issue_type === "pp_greater_than_mrp" && (
            <Card>
              <CardContent className="p-4 space-y-2">
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div>
                    <Label className="text-xs text-muted-foreground">Product</Label>
                    <p className="font-medium">{selectedItem.detected_values?.product_name}</p>
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">Auto Barcode</Label>
                    <p className="font-mono">{selectedItem.detected_values?.auto_barcode}</p>
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">Manual Barcode</Label>
                    <p className="font-mono">{selectedItem.detected_values?.manual_barcode || "N/A"}</p>
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">Brand / Group</Label>
                    <p>{selectedItem.detected_values?.brand} / {selectedItem.detected_values?.group}</p>
                  </div>
                </div>
                <Separator />
                <div className="grid grid-cols-3 gap-2 text-sm">
                  <div className="bg-red-50 p-2 rounded">
                    <Label className="text-xs text-red-600">MRP</Label>
                    <p className="font-bold text-red-700">₹{selectedItem.detected_values?.mrp}</p>
                  </div>
                  <div className="bg-orange-50 p-2 rounded">
                    <Label className="text-xs text-orange-600">Purchase Price</Label>
                    <p className="font-bold text-orange-700">₹{selectedItem.detected_values?.purchase_price}</p>
                  </div>
                  <div className="bg-yellow-50 p-2 rounded">
                    <Label className="text-xs text-yellow-600">Difference</Label>
                    <p className="font-bold text-yellow-700">
                      ₹{selectedItem.detected_values?.difference} ({selectedItem.detected_values?.percentage_over}%)
                    </p>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div>
                    <Label className="text-xs text-muted-foreground">Default Price</Label>
                    <p>₹{selectedItem.detected_values?.default_price || 0}</p>
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">Last Purchase Cost</Label>
                    <p>₹{selectedItem.detected_values?.last_purchase_cost || 0}</p>
                  </div>
                </div>
                <Separator />
                <div>
                  <Label className="text-xs text-muted-foreground">Stock at Detection</Label>
                  <p>{selectedItem.detected_values?.current_stock || 0} units</p>
                  {selectedItem.detected_values?.stock_by_shop && Object.keys(selectedItem.detected_values.stock_by_shop).length > 0 && (
                    <div className="text-xs text-muted-foreground mt-1">
                      {Object.entries(selectedItem.detected_values.stock_by_shop).map(([shop, qty]) => (
                        <span key={shop} className="mr-2">{shop}: {qty}</span>
                      ))}
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          )}
          
          {selectedItem.issue_type === "potential_duplicate" && (
            <div className="space-y-3">
              <p className="text-sm text-muted-foreground">
                {selectedItem.detected_values?.count} products with same name and MRP (₹{selectedItem.detected_values?.mrp})
              </p>
              {selectedItem.detected_values?.products?.map((p, i) => (
                <Card key={i}>
                  <CardContent className="p-4 space-y-2">
                    <div className="flex justify-between items-start">
                      <div>
                        <p className="font-medium">{p.product || selectedItem.detected_values?.product_name}</p>
                        <p className="text-xs font-mono text-muted-foreground">BC: {p.auto_barcode}</p>
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => viewProduct(p.auto_barcode)}
                      >
                        <Eye className="h-3 w-3 mr-1" /> View
                      </Button>
                    </div>
                    <div className="grid grid-cols-4 gap-2 text-xs">
                      <div>
                        <Label className="text-muted-foreground">Stock</Label>
                        <p className="font-medium">{p.stock || 0}</p>
                      </div>
                      <div>
                        <Label className="text-muted-foreground">Sales</Label>
                        <p className="font-medium">{p.sales_count || 0}</p>
                      </div>
                      <div>
                        <Label className="text-muted-foreground">PP</Label>
                        <p className="font-medium">₹{p.purchase_price || 0}</p>
                      </div>
                      <div>
                        <Label className="text-muted-foreground">Manual BC</Label>
                        <p className="font-mono truncate">{p.manual_barcode || "N/A"}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
          
          {selectedItem.issue_type === "mrp_zero_or_missing" && (
            <Card>
              <CardContent className="p-4 space-y-2">
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div>
                    <Label className="text-xs text-muted-foreground">Product</Label>
                    <p className="font-medium">{selectedItem.detected_values?.product_name}</p>
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">Auto Barcode</Label>
                    <p className="font-mono">{selectedItem.detected_values?.auto_barcode}</p>
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-2 text-sm">
                  <div className="bg-red-50 p-2 rounded">
                    <Label className="text-xs text-red-600">MRP</Label>
                    <p className="font-bold text-red-700">₹{selectedItem.detected_values?.mrp || 0}</p>
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">Purchase Price</Label>
                    <p>₹{selectedItem.detected_values?.purchase_price || 0}</p>
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">Stock</Label>
                    <p>{selectedItem.detected_values?.current_stock || 0}</p>
                  </div>
                </div>
                {selectedItem.detected_values?.is_service_item && (
                  <Badge variant="outline" className="bg-blue-50 text-blue-700">
                    Service/Non-Inventory Item
                  </Badge>
                )}
              </CardContent>
            </Card>
          )}
          
          {selectedItem.issue_type === "shared_manual_barcode" && (
            <div className="space-y-3">
              <Card className="bg-blue-50">
                <CardContent className="p-4">
                  <Label className="text-xs text-blue-600">Shared Manual Barcode</Label>
                  <p className="font-mono font-bold text-blue-800">{selectedItem.detected_values?.manual_barcode}</p>
                  <p className="text-xs text-muted-foreground mt-1">
                    Shared by {selectedItem.detected_values?.count} products
                    {selectedItem.detected_values?.same_name && selectedItem.detected_values?.same_mrp && " (identical)"}
                  </p>
                </CardContent>
              </Card>
              {selectedItem.detected_values?.products?.map((p, i) => (
                <Card key={i}>
                  <CardContent className="p-4 space-y-2">
                    <div className="flex justify-between items-start">
                      <div>
                        <p className="font-medium">{p.product}</p>
                        <p className="text-xs font-mono text-muted-foreground">Auto BC: {p.auto_barcode}</p>
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => viewProduct(p.auto_barcode)}
                      >
                        <Eye className="h-3 w-3 mr-1" /> View
                      </Button>
                    </div>
                    <div className="grid grid-cols-3 gap-2 text-xs">
                      <div>
                        <Label className="text-muted-foreground">MRP</Label>
                        <p className="font-medium">₹{p.mrp || 0}</p>
                      </div>
                      <div>
                        <Label className="text-muted-foreground">Stock</Label>
                        <p className="font-medium">{p.stock || 0}</p>
                      </div>
                      <div>
                        <Label className="text-muted-foreground">Brand</Label>
                        <p className="font-medium">{p.brand_name || "N/A"}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
        
        <Separator className="my-4" />
        
        {/* Current Product State (for comparison) */}
        {selectedItem.current_products && selectedItem.current_products.length > 0 && (
          <>
            <div className="space-y-4">
              <h4 className="font-semibold text-sm flex items-center gap-2">
                <Package className="h-4 w-4" />
                Current Product State
              </h4>
              {selectedItem.current_products.map((p, i) => (
                <Card key={i} className="border-dashed">
                  <CardContent className="p-4 space-y-2">
                    <div className="flex justify-between items-start">
                      <div>
                        <p className="font-medium">{p.product}</p>
                        <p className="text-xs font-mono text-muted-foreground">BC: {p.auto_barcode}</p>
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => viewProduct(p.auto_barcode)}
                      >
                        <Eye className="h-3 w-3 mr-1" /> View Product
                      </Button>
                    </div>
                    <div className="grid grid-cols-4 gap-2 text-xs">
                      <div>
                        <Label className="text-muted-foreground">MRP</Label>
                        <p className="font-medium">₹{p.mrp || 0}</p>
                      </div>
                      <div>
                        <Label className="text-muted-foreground">PP</Label>
                        <p className="font-medium">₹{p.purchase_price || 0}</p>
                      </div>
                      <div>
                        <Label className="text-muted-foreground">Stock</Label>
                        <p className="font-medium">{p.current_stock || 0}</p>
                      </div>
                      <div>
                        <Label className="text-muted-foreground">Sales</Label>
                        <p className="font-medium">{p.sales_count || 0}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
            <Separator className="my-4" />
          </>
        )}
        
        {/* Notes */}
        <div className="space-y-4">
          <h4 className="font-semibold text-sm flex items-center gap-2">
            <MessageSquare className="h-4 w-4" />
            Owner Review Notes
          </h4>
          <Textarea
            placeholder="Add notes about your review decision..."
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={3}
          />
          <Button variant="outline" size="sm" onClick={handleSaveNotes}>
            Save Notes
          </Button>
        </div>
        
        <Separator className="my-4" />
        
        {/* Status Actions */}
        <div className="space-y-4 pb-8">
          <h4 className="font-semibold text-sm">Change Review Status</h4>
          <p className="text-xs text-muted-foreground">
            Changing status does NOT modify product data. Use the product editing interface for corrections.
          </p>
          <div className="flex flex-wrap gap-2">
            {selectedItem.status === "new" && (
              <>
                <Button
                  variant="outline"
                  onClick={() => { setPendingStatus("under_review"); setStatusDialogOpen(true); }}
                >
                  <Clock className="h-4 w-4 mr-1" /> Mark Under Review
                </Button>
                <Button
                  variant="outline"
                  onClick={() => { setPendingStatus("dismissed"); setStatusDialogOpen(true); }}
                >
                  <XCircle className="h-4 w-4 mr-1" /> Dismiss
                </Button>
              </>
            )}
            {selectedItem.status === "under_review" && (
              <>
                <Button
                  variant="outline"
                  className="border-green-300 text-green-700"
                  onClick={() => { setPendingStatus("reviewed"); setStatusDialogOpen(true); }}
                >
                  <CheckCircle2 className="h-4 w-4 mr-1" /> Mark as Reviewed
                </Button>
                <Button
                  variant="outline"
                  onClick={() => { setPendingStatus("dismissed"); setStatusDialogOpen(true); }}
                >
                  <XCircle className="h-4 w-4 mr-1" /> Dismiss
                </Button>
              </>
            )}
            {(selectedItem.status === "reviewed" || selectedItem.status === "dismissed") && (
              <Button
                variant="outline"
                onClick={() => { setPendingStatus("new"); setStatusDialogOpen(true); }}
              >
                Reopen as New
              </Button>
            )}
          </div>
        </div>
      </ScrollArea>
    );
  };
  
  return (
    <div className="p-4 md:p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold" data-testid="product-data-review-title">
            Product Data Review
          </h1>
          <p className="text-muted-foreground text-sm">
            Review product master data exceptions. Corrections must be done manually.
          </p>
        </div>
        <Button onClick={handleScan} disabled={scanning} data-testid="scan-button">
          {scanning ? <RefreshCw className="h-4 w-4 mr-2 animate-spin" /> : <Scan className="h-4 w-4 mr-2" />}
          {scanning ? "Scanning..." : "Scan for Issues"}
        </Button>
      </div>
      
      {/* Summary Cards */}
      {renderSummary()}
      
      {/* Category Badges */}
      {renderCategoryBadges()}
      
      {/* Filters */}
      <div className="flex flex-col md:flex-row gap-3 mb-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search product name, barcode..."
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            className="pl-9"
            data-testid="search-input"
          />
        </div>
        <Select value={severityFilter} onValueChange={(v) => { setSeverityFilter(v); setPage(1); }}>
          <SelectTrigger className="w-[140px]" data-testid="severity-filter">
            <SelectValue placeholder="Severity" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Severities</SelectItem>
            <SelectItem value="critical">Critical</SelectItem>
            <SelectItem value="high">High</SelectItem>
            <SelectItem value="medium">Medium</SelectItem>
            <SelectItem value="low">Low</SelectItem>
            <SelectItem value="info">Info</SelectItem>
          </SelectContent>
        </Select>
        <Button variant="outline" onClick={() => { setSearch(""); setIssueTypeFilter("all"); setSeverityFilter("all"); setStatusFilter("all"); setPage(1); }}>
          Clear Filters
        </Button>
      </div>
      
      {/* Items Table */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-4 space-y-3">
              {[1, 2, 3, 4, 5].map(i => (
                <Skeleton key={i} className="h-16 w-full" />
              ))}
            </div>
          ) : items.length === 0 ? (
            <div className="p-8 text-center text-muted-foreground">
              <Package className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>No review items found.</p>
              <p className="text-sm">Click &quot;Scan for Issues&quot; to detect product data exceptions.</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[100px]">Severity</TableHead>
                  <TableHead className="w-[120px]">Type</TableHead>
                  <TableHead>Product</TableHead>
                  <TableHead>Reason</TableHead>
                  <TableHead className="w-[120px]">Status</TableHead>
                  <TableHead className="w-[100px]">Detected</TableHead>
                  <TableHead className="w-[60px]"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map(renderItem)}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
      
      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex justify-center items-center gap-4 mt-4">
          <Button
            variant="outline"
            size="sm"
            disabled={page <= 1}
            onClick={() => setPage(p => p - 1)}
          >
            <ChevronLeft className="h-4 w-4" /> Previous
          </Button>
          <span className="text-sm text-muted-foreground">
            Page {page} of {totalPages}
          </span>
          <Button
            variant="outline"
            size="sm"
            disabled={page >= totalPages}
            onClick={() => setPage(p => p + 1)}
          >
            Next <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      )}
      
      {/* Detail Sheet */}
      <Sheet open={detailOpen} onOpenChange={setDetailOpen}>
        <SheetContent className="w-full sm:max-w-xl">
          <SheetHeader>
            <SheetTitle>Review Item Details</SheetTitle>
            <SheetDescription>
              Review the detected issue and update status. Product data changes must be done manually.
            </SheetDescription>
          </SheetHeader>
          {renderDetailContent()}
        </SheetContent>
      </Sheet>
      
      {/* Status Change Confirmation Dialog */}
      <Dialog open={statusDialogOpen} onOpenChange={setStatusDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirm Status Change</DialogTitle>
            <DialogDescription>
              Update the review status. This does not modify product data.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="text-sm text-muted-foreground flex items-center gap-2">
              <span>Change status to:</span>
              <Badge variant="outline" className={STATUS_CONFIG[pendingStatus]?.color}>
                {STATUS_CONFIG[pendingStatus]?.label}
              </Badge>
            </div>
            <div className="bg-yellow-50 border border-yellow-200 rounded p-3">
              <div className="text-sm text-yellow-800">
                <AlertTriangle className="h-4 w-4 inline mr-1" />
                This does NOT modify any product data. To correct the product, use the &quot;View Product&quot; button 
                and edit it through the normal product editing interface.
              </div>
            </div>
            <div>
              <Label>Notes (optional)</Label>
              <Textarea
                placeholder="Add notes about your decision..."
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={3}
                data-testid="status-change-notes"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setStatusDialogOpen(false)} data-testid="status-change-cancel">
              Cancel
            </Button>
            <Button onClick={handleStatusChange} data-testid="status-change-confirm">
              Confirm
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
